import { z } from "zod";
import { PublicUser } from "./users.ts";
import { PASSWORD_MIN, password } from "./password.ts";
export { PASSWORD_MIN, password };

export const CODE_LENGTH = 6;

const email = z.email().transform((v) => v.trim().toLowerCase());
const name = z.string().trim().min(1).max(80);
const sixDigits = z.string().regex(/^\d{6}$/, "code must be 6 digits");

/** Status the UI asks for before rendering anything auth-related. */
export const AuthStatus = z.object({
  setupRequired: z.boolean(),
  user: PublicUser.nullable(),
  /** "Continue with Google" is offered on the login page. */
  googleSignIn: z.boolean().default(false),
  /** One project and its tools only (Codvero Lab): the home page opens that project. */
  singleProject: z.boolean().default(false),
  /** "lab": Codvero Lab (named so, and marked on the logo). */
  edition: z.enum(["lab"]).nullable().default(null),
});

/** Admin policy for Google sign-in. Users without an account still need an invite unless autoRegister matches their domain. */
export const GoogleSignInSettings = z.object({
  enabled: z.boolean().default(false),
  autoRegister: z.boolean().default(false),
  allowedDomains: z.array(z.string().trim().toLowerCase().regex(/^[a-z0-9.-]+\.[a-z]{2,}$/)).max(20).default([]),
});
export type GoogleSignInSettings = z.infer<typeof GoogleSignInSettings>;
export const GoogleTotpInput = z.object({ token: z.string().min(16), code: sixDigits });
export type AuthStatus = z.infer<typeof AuthStatus>;

/** First run: creates the admin account. Only allowed while there are zero users. */
export const SetupInput = z.object({ email, name, password });
export type SetupInput = z.infer<typeof SetupInput>;

/** Step 1 of registration: invite code + account details. Sends a verification code by email. */
export const RegisterInput = z.object({
  inviteCode: z.string().trim().min(8).max(64),
  email,
  name,
  password,
});
export type RegisterInput = z.infer<typeof RegisterInput>;

export const RegisterResponse = z.object({
  pendingUserId: z.string(),
  /** Masked email the code was sent to, for display. */
  sentTo: z.string(),
  expiresAt: z.string(),
});
export type RegisterResponse = z.infer<typeof RegisterResponse>;

/** Step 2 of registration: the 6-digit code from the email. Activates the account and signs in. */
export const VerifyEmailInput = z.object({ pendingUserId: z.string(), code: sixDigits });
export type VerifyEmailInput = z.infer<typeof VerifyEmailInput>;

export const ResendCodeInput = z.object({ pendingUserId: z.string() });

export const LoginInput = z.object({
  email,
  password,
  /** Required only when the account has TOTP enabled. */
  totpCode: sixDigits.optional(),
});
export type LoginInput = z.infer<typeof LoginInput>;

export const LoginResponse = z.discriminatedUnion("status", [
  z.object({ status: z.literal("ok"), user: PublicUser }),
  z.object({ status: z.literal("totp_required") }),
  z.object({ status: z.literal("email_not_verified"), pendingUserId: z.string() }),
]);
export type LoginResponse = z.infer<typeof LoginResponse>;

/** TOTP enrolment: server returns the secret and otpauth URL; client confirms with one code. */
export const TotpSetupResponse = z.object({ secret: z.string(), otpauthUrl: z.string() });
export const TotpConfirmInput = z.object({ code: sixDigits });
export const TotpDisableInput = z.object({ password, code: sixDigits });

export const ChangePasswordInput = z.object({ currentPassword: password, newPassword: password });
export type ChangePasswordInput = z.infer<typeof ChangePasswordInput>;
